import { Route } from '@angular/router';
import { ROUTE_NAMES } from '@app/core/navigation/navigation.constants';
import timesheetRoutes from '@timesheet/timesheet.routes';
import vesselReportRoutes from '@vessel-reports/vessel-reports.routes';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/auth.guard';
import { AuthLanding } from 'app/core/auth/auth.landing';
import { NoAuthGuard } from 'app/core/auth/noAuth.guard';
import { RedirectHandlerComponent } from 'app/features/auth/redirect/redirect.component';
import { LayoutComponent } from 'app/layout/layout.component';

export const appRoutes: Route[] = [
  { path: '', pathMatch: 'full', canActivate: [AuthLanding], children: [] },
  { path: ROUTE_NAMES.SIGNED_IN_REDIRECT, pathMatch: 'full', canActivate: [AuthLanding], children: [] },
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      {
        path: ROUTE_NAMES.LOGIN,
        children: [
          { path: ROUTE_NAMES.MVP_REDIRECT, component: RedirectHandlerComponent },
          { path: ROUTE_NAMES.CHEVRON_REDIRECT, component: RedirectHandlerComponent },
          { path: ROUTE_NAMES.SHARE_COOKIE_REDIRECT, component: RedirectHandlerComponent }
        ]
      },
      { path: ROUTE_NAMES.SIGN_IN, loadChildren: () => import('app/features/auth/sign-in/sign-in.routes') },
      {
        path: ROUTE_NAMES.RESET_PASSWORD,
        loadComponent: () => import('app/features/auth/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent)
      }
    ]
  },
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [{ path: ROUTE_NAMES.SIGN_OUT, loadChildren: () => import('app/features/auth/sign-out/sign-out.routes') }]
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    resolve: {
      initialData: initialDataResolver
    },
    children: [
      {
        path: ROUTE_NAMES.ANALYTICS,
        children: [
          {
            path: ROUTE_NAMES.LIVE_VESSEL,
            loadChildren: () => import('@analytics/live-vessel/live-vessel.routes'),
            data: {
              layout: 'empty'
            }
          },
          {
            path: ROUTE_NAMES.LIVE_TERMINAL,
            loadChildren: () => import('@analytics/live-terminal/live-terminal.routes'),
            data: {
              layout: 'empty'
            }
          },
          {
            path: ROUTE_NAMES.BUNKER_ANALYSIS,
            loadChildren: () => import('@bunker-analysis/bunker-analysis.routes')
          }
        ]
      },
      {
        path: ROUTE_NAMES.DATABASE,
        children: [
          { path: ROUTE_NAMES.COMPANY, loadChildren: () => import('@company/company.routes') },
          { path: ROUTE_NAMES.USER, loadChildren: () => import('@user/user.routes') },
          { path: ROUTE_NAMES.VESSEL, loadChildren: () => import('@vessel/vessel.routes') },
          { path: ROUTE_NAMES.CARGO, loadChildren: () => import('app/features/admin/database/cargo/cargo.routes') },
          { path: ROUTE_NAMES.PORT, loadChildren: () => import('@port/port.routes') },
          { path: ROUTE_NAMES.LOCATION, loadChildren: () => import('@location/location.routes') },
          { path: ROUTE_NAMES.DEPARTMENT, loadChildren: () => import('@department/department.routes') },
          { path: ROUTE_NAMES.BANK, loadChildren: () => import('@bank/bank.routes') }
        ]
      },
      {
        path: ROUTE_NAMES.PORT_EXPENSE,
        children: [
          {
            path: ROUTE_NAMES.DA_MANAGEMENT,
            loadChildren: () => import('@port-expense/disbursement/disbursement.routes')
          },
          { path: ROUTE_NAMES.OWNERS_ACCOUNT, loadChildren: () => import('@port-expense/oa-ca/oa-ca.routes') },
          { path: ROUTE_NAMES.SOA, loadChildren: () => import('@analytics/port-expense-analysis/soa/soa.routes') }
        ]
      },
      {
        path: ROUTE_NAMES.PORT_OPERATION,
        children: [
          { path: ROUTE_NAMES.CTM, loadChildren: () => import('@cash-to-master/ctm.routes') },
          { path: ROUTE_NAMES.PORTCALL, loadChildren: () => import('@portcall/portcall.routes') },
          { path: ROUTE_NAMES.TERMINAL_DEMURRAGE, loadChildren: () => import('@demurrage/demurrage.routes') }
        ]
      },
      {
        path: ROUTE_NAMES.CHARTERING,
        children: [
          { path: ROUTE_NAMES.VOYAGE_CHARTER, loadChildren: () => import('@voyage-charter/voyage-charter.routes') },
          { path: ROUTE_NAMES.VC_INVOICES, loadChildren: () => import('@vc-invoices/vc-invoices.routes') },
          { path: ROUTE_NAMES.TIME_CHARTER, loadChildren: () => import('@time-charter/time-charter.routes') },
          { path: ROUTE_NAMES.TC_INVOICES, loadChildren: () => import('@tc-invoices/tc-invoices.routes') },
          {
            path: ROUTE_NAMES.VESSEL_REPORT_CHARTERING,
            loadChildren: () => import('@chartering/vessel-reports/vessel-reports.routes')
          },
          {
            path: ROUTE_NAMES.INVENTORY_SUPPLY,
            loadChildren: () => import('app/features/admin/chartering/bunker-lube-fw/bunker-lube-fw.routes')
          },
          {
            path: ROUTE_NAMES.VESSEL_SCHEDULE,
            loadChildren: () => import('@vessel-schedule/vessel-schedule.routes')
          },
          {
            path: ROUTE_NAMES.BUNKER_SUPPLY,
            loadChildren: () => import('@chartering/bunker-supply/bunker-supply.routes')
          }
        ]
      },
      {
        path: ROUTE_NAMES.SHIP_SHORE_OPERATION,
        children: [
          { path: ROUTE_NAMES.VESSEL_CALL, loadChildren: () => import('@vessel-call/vessel-call.routes') },
          {
            path: ROUTE_NAMES.PRE_ARRIVAL_NOTICE,
            loadChildren: () => import('@pre-arrival-notice/pre-arrival-notice.routes')
          },
          {
            path: ROUTE_NAMES.PRE_BERTH_QUESTIONNAIRE,
            loadChildren: () => import('app/features/admin/ship-shore-operation/pre-berth-questionnaire/pre-berth-questionnaire.routes')
          }
        ]
      },
      {
        path: ROUTE_NAMES.SHIP_INTERFACE,
        children: [
          {
            path: ROUTE_NAMES.ITINERARY,
            loadChildren: () => import('@vessel-itinerary/itinerary.routes')
          },
          {
            path: ROUTE_NAMES.VESSEL_REPORT_SHIP_INTERFACE,
            children: vesselReportRoutes
          },
          {
            path: ROUTE_NAMES.TIMESHEET,
            children: timesheetRoutes
          },
          {
            path: ROUTE_NAMES.REQUISITIONS,
            loadChildren: () => import('@requisitions/requisitions.routes')
          }
        ]
      },
      {
        path: ROUTE_NAMES.BILLING,
        children: [
          {
            path: ROUTE_NAMES.INVOICE_MANAGEMENT,
            loadChildren: () => import('@invoices/invoices.routes')
          },
          {
            path: ROUTE_NAMES.ELXA_INVOICES,
            loadChildren: () => import('app/features/admin/billing/elxa-invoices/elxa-invoices.routes')
          },
          {
            path: ROUTE_NAMES.APPROVAL_BOARD_OWNER,
            loadChildren: () => import('@approval-board/approval-board.routes')
          },
          { path: ROUTE_NAMES.APPROVAL_BOARD_ELXA, loadChildren: () => import('@elxa-approval/elxa-approval.routes') },
          { path: ROUTE_NAMES.EXCHANGE_RATE, loadChildren: () => import('@exchange-rate/exchange-rate.routes') },
          {
            path: ROUTE_NAMES.PROCESSED_PAYMENTS,
            loadChildren: () => import('@processed-payments/processed-payment.routes')
          },
          { path: ROUTE_NAMES.CREDIT_BALANCE, loadChildren: () => import('@credit-balance/credit-balance.routes') },
          { path: ROUTE_NAMES.FLOATING_ACCOUNT, loadChildren: () => import('@floating-accounts/floating-accounts.routes') }
        ]
      },
      {
        path: ROUTE_NAMES.ELXA_CONTROLS,
        children: [
          { path: ROUTE_NAMES.CTM_CONTROLS, loadChildren: () => import('@ctm-tariff/ctm-tariff.routes') },
          {
            path: ROUTE_NAMES.PORT_EXPENSE_CONTROLS,
            loadChildren: () => import('@port-expense-controls/port-expense-controls.routes')
          },
          {
            path: ROUTE_NAMES.PRE_BERTH_QUESTIONNAIRE_FORM,
            loadChildren: () => import('app/features/admin/elxa-controls/pbq-form/pre-berth-questionnaire-routes')
          },
          { path: ROUTE_NAMES.XERO_CONTROLS, loadChildren: () => import('@xero-controls/xero-controls.routes') },
          {
            path: ROUTE_NAMES.SUBSCRIPTIONS,
            loadChildren: () => import('@app/features/admin/elxa-controls/subscriptions/subscriptions.routes')
          }
        ]
      },
      {
        path: ROUTE_NAMES.PAGES,
        children: [{ path: 'settings', loadChildren: () => import('@app/features/admin/settings/settings.routes') }]
      }
    ]
  },
  { path: ROUTE_NAMES.NO_PERMISSION, redirectTo: ROUTE_NAMES.NOT_FOUND },
  { path: ROUTE_NAMES.NOT_FOUND, pathMatch: 'full', redirectTo: `${ROUTE_NAMES.DATABASE}/${ROUTE_NAMES.COMPANY}` },
  { path: '**', redirectTo: `${ROUTE_NAMES.DATABASE}/${ROUTE_NAMES.COMPANY}` }
];
